// Tarefas de caça (bounty tasks), tarefas semanais, loja de tarefas e o Bounty Talisman, dentro da janela de Prey.
// Visual "caçador": contratos de caça em couro, verde-musgo e âmbar (CSS em client/css-parts/prey.css, prefixo .bt-).
import { useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { BOUNTY_SHOP, type BountyState } from '../../../../shared/src/game/bounty';
import type { Outfit } from '../../../../shared/src/protocol';
import { Portrait } from './BattleList';
import { ItemImg } from './ItemSlot';
import { HTP_SHOP, TALISMAN, TALISMAN_MAX_LEVEL, WEEKLY_DELIVERY_HTP, WEEKLY_DIFF_NAMES, WEEKLY_HTP, talismanCost, talismanValue, weeklyMult, weeklyXp, type TalismanKey } from '../../../../shared/src/game/tasks';

export type BountySend = (op: 'pick' | 'cancel' | 'reroll' | 'buy' | 'talisman' | 'htpbuy' | 'deliver', arg?: string) => void;
type Info = Record<string, { need: number; points: number; xp: number }>;
const n = (v: number) => v.toLocaleString('pt-BR');
const hm = (s: number) => `${Math.floor(s / 3600)}h${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`;
const pct = (v: number) => String(v).replace('.', ',');
const TB = '/assets/interface/images/taskboard';
const PTS = <img class="bt-cur" src={`${TB}/icon-currency-bountypoints.png`} alt="" />;

/** passo a passo: abre sozinho até o jogador fechar uma vez */
function useHelp(key: string, auto: boolean): [boolean, () => void] {
  const k = 'nx-help-' + key;
  const [h, setH] = useState<boolean | null>(null);
  let seen = false;
  try { seen = localStorage.getItem(k) === '1'; } catch { /* sem storage */ }
  const show = h ?? (auto && !seen);
  return [show, () => { try { localStorage.setItem(k, '1'); } catch { /* sem storage */ } setH(!show); }];
}

function Head(props: { icon: string; title: string; sub: string; onHelp?: () => void; tags?: string[]; children?: ComponentChildren }) {
  return (
    <div class="bt-head">
      <div class="bt-claws"><i /><i /><i /></div>
      <div class="bt-emblem"><img src={`${TB}/${props.icon}`} alt="" /></div>
      <div class="bt-head-txt">
        <div class="bt-title">{props.title} {props.onHelp && <button class="bt-help" onClick={props.onHelp} title="Como funciona">?</button>}</div>
        <div class="bt-sub">{props.sub}</div>
        {props.tags && <div class="bt-tags">{props.tags.map((t) => <span key={t}>{t}</span>)}</div>}
      </div>
      {props.children && <div class="bt-head-side">{props.children}</div>}
    </div>
  );
}

function Bar(props: { v: number; max: number; label: string; done?: boolean }) {
  const f = props.max > 0 ? Math.max(0, Math.min(1, props.v / props.max)) : 0;
  return <div class={'bt-bar' + (props.done ? ' done' : '')}><i style={{ width: `${f * 100}%` }} /><span>{props.label}</span></div>;
}

function Steps(props: { items: [string, string, string][] }) {
  return <div class="bt-steps">{props.items.map(([i, b, s]) => <div key={b}><i>{i}</i><b>{b}</b><small>{s}</small></div>)}</div>;
}

export function BountyTasks(props: { st: BountyState; info: Info; rerollGold: number; outfitOf: (name: string) => Outfit | undefined; send: BountySend; onHunt?: (monster: string) => void }) {
  const { st, info } = props;
  const t = st.task;
  const free = Date.now() >= st.freeAt;
  const [help, toggle] = useHelp('bounty', !(st.done ?? 0) && !t);
  const ti = t ? info[t.monster] : undefined;
  const tp = t ? Math.min(100, Math.floor((t.kills / Math.max(1, t.need)) * 100)) : 0;
  return (
    <div class="bt">
      <Head icon="icon-bountytasks.png" title="Tarefas de caça" sub="Aceite um contrato. Traga as cabeças. Receba a recompensa." onHelp={toggle}
        tags={['1 contrato por vez', 'Outras opções grátis a cada 20 horas', 'Pontos melhoram o Talismã']} />
      <div class="bt-stats">
        <div class="bt-stat amber"><small>Pontos de tarefa</small><b>{PTS}{n(st.points)}</b><span>para o Talismã</span></div>
        <div class="bt-stat"><small>Contratos cumpridos</small><b>{n(st.done ?? 0)}</b><span>no total</span></div>
        <div class={'bt-stat' + (t ? ' moss' : '')}><small>Contrato atual</small><b class="bt-sm">{t ? `${tp}%` : 'Nenhum'}</b><span>{t ? t.monster : 'escolha abaixo'}</span></div>
      </div>
      {help && <Steps items={[
        ['📜', '1. Escolha o contrato', 'São 3 criaturas para escolher. Cada uma mostra quantas mortes pede e o que paga.'],
        ['🗡️', '2. Cace', 'Mate a quantidade pedida em qualquer hunt. O progresso aparece aqui.'],
        ['💰', '3. Receba', 'Ao terminar você ganha experiência e pontos de tarefa automaticamente.'],
        ['🔮', '4. Talismã', 'Gaste os pontos na aba Talismã: bônus contra a criatura do contrato atual.'],
      ]} />}

      {t ? (
        <div class="bt-contract active">
          <span class="bt-seal">✔</span>
          <span class="bt-face big">{props.outfitOf(t.monster) ? <Portrait outfit={props.outfitOf(t.monster)!} size={64} /> : <i>?</i>}</span>
          <div class="bt-ctxt">
            <small class="bt-kicker">Contrato em andamento</small>
            <b class="bt-mname">{t.monster}</b>
            <Bar v={t.kills} max={t.need} label={`${n(t.kills)} / ${n(t.need)} abates · faltam ${n(Math.max(0, t.need - t.kills))}`} done={t.kills >= t.need} />
            {ti && <div class="bt-reward"><span>{PTS}<b>{n(ti.points)}</b> pontos</span><span>✨ <b>{n(ti.xp)}</b> XP</span><em>(XP antes do bônus do level)</em></div>}
            <div class="bt-acts">{props.onHunt && t.kills < t.need && <button class="tb-button green" onClick={() => props.onHunt!(t.monster)} title="Abre o Mapa de Caças na hunt com mais dessa criatura">🗺️ Ir para a hunt</button>}
              <button class="tb-button bt-danger" onClick={() => props.send('cancel')}>Cancelar contrato (perde o progresso)</button></div>
          </div>
        </div>
      ) : (
        <>
          <div class="bt-sec">Contratos disponíveis <small>· escolha 1 de {st.offer.length}</small></div>
          <div class="bt-offer">
            {st.offer.map((m, i) => {
              const o = props.outfitOf(m);
              const inf = info[m];
              return (
                <div key={m} class="bt-contract card" style={{ '--bt-tilt': `${(i - 1) * 0.6}deg` } as any}>
                  <span class="bt-pin" />
                  <small class="bt-kicker">Procura-se</small>
                  <span class="bt-face">{o ? <Portrait outfit={o} size={56} /> : <i>?</i>}</span>
                  <b class="bt-mname">{m}</b>
                  {inf ? (
                    <div class="bt-terms">
                      <span><em>Matar</em><b>{n(inf.need)}</b></span>
                      <span><em>Pontos</em><b class="amber">{n(inf.points)}</b></span>
                      <span><em>XP</em><b class="moss">{n(inf.xp)}</b></span>
                    </div>
                  ) : <small class="bt-muted">Sem detalhes</small>}
                  <button class="tb-button green" onClick={() => props.send('pick', m)}>Aceitar contrato</button>
                </div>
              );
            })}
            {!st.offer.length && <p class="bt-muted">Nenhum contrato no quadro. Peça outras opções.</p>}
          </div>
          <div class="bt-foot">
            <span>{free ? <>Trocar os contratos está <b class="moss">grátis</b> agora.</> : <>Troca grátis de novo em <b>{hm(Math.ceil((st.freeAt - Date.now()) / 1000))}</b>.</>}</span>
            <button class="tb-button" onClick={() => props.send('reroll')}><img class="bt-cur" src={`${TB}/icon-currency-taskreroll.png`} alt="" /> {free ? 'Outras opções (grátis)' : `Outras opções: ${n(props.rerollGold)} gp`}</button>
          </div>
        </>
      )}
    </div>
  );
}

function ShopGrid(props: { items: { id: string; name: string; cost: number; desc: string }[]; have: number; onBuy: (id: string) => void }) {
  return (
    <div class="bt-shop">
      {props.items.map((it) => {
        const ok = props.have >= it.cost;
        return (
          <div key={it.id} class={'bt-item' + (ok ? '' : ' locked')}>
            <div><b>{it.name}</b><small>{it.desc}</small>{!ok && <em class="bt-why">Faltam {n(it.cost - props.have)} pontos</em>}</div>
            <button class="tb-button green" disabled={!ok} onClick={() => props.onBuy(it.id)}>{n(it.cost)} pontos</button>
          </div>
        );
      })}
    </div>
  );
}

export function BountyShop(props: { points: number; send: BountySend }) {
  return (
    <div class="bt">
      <Head icon="icon-huntingtaskshop.png" title="Loja de tarefas" sub="Troque os pontos das tarefas de caça.">
        <div class="bt-purse">{PTS}<b>{n(props.points)}</b><small>pontos</small></div>
      </Head>
      <ShopGrid items={BOUNTY_SHOP} have={props.points} onBuy={(id) => props.send('buy', id)} />
    </div>
  );
}

const TL_ICON: Record<TalismanKey, string> = { leech: 'lifeleech', dmg: 'damageagainstmonster', loot: 'moreloot', bestiary: 'doublebestiarychance' };

export function Talisman(props: { st: BountyState; send: BountySend }) {
  const tl = props.st.board?.talisman ?? {};
  const task = props.st.task?.monster;
  const keys = Object.keys(TALISMAN) as TalismanKey[];
  const total = keys.reduce((s, k) => s + (tl[k] ?? 0), 0);
  const [help, toggle] = useHelp('talisman', total === 0);
  return (
    <div class="bt">
      <Head icon="icon-bountyring-damageagainstmonster.png" title="Bounty Talisman" sub="O amuleto do caçador: força extra contra a presa do contrato." onHelp={toggle}
        tags={['Níveis ficam no personagem', `Máximo nível ${TALISMAN_MAX_LEVEL}`]}>
        <div class="bt-purse">{PTS}<b>{n(props.st.points)}</b><small>pontos</small></div>
      </Head>
      <div class={'bt-target' + (task ? ' on' : '')}>
        {task ? <>🎯 Talismã ativo contra <b>{task}</b> (seu contrato atual).</> : <>💤 Sem contrato ativo: o talismã fica adormecido até você aceitar uma tarefa de caça.</>}
      </div>
      {help && <Steps items={[
        ['🎯', '1. Tenha um contrato', 'Os bônus só valem contra a criatura da tarefa de caça atual.'],
        ['🔮', '2. Melhore', `Cada nível custa pontos de tarefa: ${talismanCost(0)} no primeiro e +12 a cada nível.`],
        ['📈', '3. Teto suave', 'Depois de certo nível o bônus continua subindo, mas mais devagar (marca na trilha).'],
      ]} />}
      <div class="bt-tali">
        {keys.map((k) => {
          const lv = tl[k] ?? 0, cost = talismanCost(lv), max = lv >= TALISMAN_MAX_LEVEL;
          const capLv = Math.round(TALISMAN[k].cap / TALISMAN[k].step);
          const ok = props.st.points >= cost;
          return (
            <div key={k} class={'bt-tl' + (max ? ' max' : '')}>
              <div class="bt-tl-icon"><img src={`${TB}/icon-bountyring-${TL_ICON[k]}.png`} alt="" /><span>{lv}</span></div>
              <div class="bt-tl-body">
                <div class="bt-tl-name"><b>{TALISMAN[k].name}</b><span class="bt-tl-val">+{pct(talismanValue(k, lv))}%</span>
                  {!max && <span class="bt-tl-next">→ +{pct(talismanValue(k, lv + 1))}%</span>}</div>
                <small>{TALISMAN[k].desc}</small>
                <div class="bt-track" title={`Nível ${lv} de ${TALISMAN_MAX_LEVEL}`}>
                  <i class="fill" style={{ width: `${(lv / TALISMAN_MAX_LEVEL) * 100}%` }} />
                  <i class="cap" style={{ left: `${(capLv / TALISMAN_MAX_LEVEL) * 100}%` }} title={`A partir do nível ${capLv} (+${pct(TALISMAN[k].cap)}%) sobe +${pct(TALISMAN[k].after)}% por nível`} />
                  {[0, 10, 20, 30, 40, 50, 60].map((m) => <b key={m} class={lv >= m ? 'on' : ''} style={{ left: `${(m / TALISMAN_MAX_LEVEL) * 100}%` }} title={`Nível ${m}: +${pct(talismanValue(k, m))}%`} />)}
                </div>
                <div class="bt-track-lbl"><span>0</span><span>teto suave: nv {capLv}</span><span>{TALISMAN_MAX_LEVEL}</span></div>
              </div>
              <div class="bt-tl-buy">
                {max ? <span class="bt-maxed">MÁXIMO</span> : <>
                  <button class="tb-button green" disabled={!ok} onClick={() => props.send('talisman', k)}>{n(cost)} pontos</button>
                  {!ok && <em class="bt-why">faltam {n(cost - props.st.points)}</em>}
                </>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const MULT_STEPS = [[0, 1], [4, 2], [8, 3], [12, 5], [16, 8]] as const;

export function Weekly(props: { st: BountyState; level: number; outfitOf: (name: string) => Outfit | undefined; send: BountySend; have: (typeId: number) => number; onHunt?: (monster: string) => void }) {
  const b = props.st.board;
  const [help, toggle] = useHelp('weekly', !!b && b.weeklyDone === 0 && !b.htpTotal);
  if (!b) return <p class="muted">Carregando...</p>;
  const d = b.diff ?? 0;
  const mult = weeklyMult(b.weeklyDone);
  const killsDone = b.weekly.filter((t) => t.kills >= t.need).length;
  const dels = b.deliveries ?? [];
  const delsDone = dels.filter((x) => x.done).length;
  const nextStep = MULT_STEPS.find(([at]) => at > b.weeklyDone);
  return (
    <div class="bt">
      <Head icon="icon-weeklytasks.png" title="Tarefas semanais" sub="Uma temporada de caça por semana. Quanto mais você cumpre, mais cada uma paga." onHelp={toggle}
        tags={['Renovam toda segunda-feira', `Dificuldade: ${WEEKLY_DIFF_NAMES[d]}`]}>
        <div class="bt-purse">{PTS}<b>{n(b.htp)}</b><small>pontos semanais</small></div>
      </Head>
      <div class="bt-stats">
        <div class={'bt-stat diff d' + d}><small>Dificuldade</small><b class="bt-sm">{WEEKLY_DIFF_NAMES[d]}</b><span>pelo seu level</span></div>
        <div class="bt-stat moss"><small>XP por tarefa</small><b class="bt-sm">{n(weeklyXp(props.level, d))}</b><span>abate concluído</span></div>
        <div class="bt-stat amber"><small>Pontos por tarefa</small><b class="bt-sm">{WEEKLY_HTP[d] * mult}</b><span>{WEEKLY_HTP[d]} × {mult} (abate)</span></div>
        <div class="bt-stat"><small>Concluídas</small><b>{b.weeklyDone}</b><span>nesta semana</span></div>
      </div>
      {help && <Steps items={[
        ['🗓️', '1. Toda segunda', 'Chegam tarefas novas de abate e de entrega, na dificuldade do seu level.'],
        ['🗡️', '2. Abates', 'Mate a quantidade pedida: ganha experiência e pontos semanais.'],
        ['🎒', '3. Entregas', `Leve os itens na mochila e clique em Entregar: ${WEEKLY_DELIVERY_HTP} pontos × multiplicador.`],
        ['✖️', '4. Multiplicador', 'Com 4, 8, 12 e 16 tarefas concluídas na semana, cada nova tarefa paga mais.'],
      ]} />}

      {/* trilha do multiplicador */}
      <div class="bt-mult">
        <div class="bt-mult-lbl"><b>Multiplicador</b><span class="bt-mult-now">x{mult}</span>
          <small>{nextStep ? `faltam ${nextStep[0] - b.weeklyDone} tarefa(s) para x${nextStep[1]}` : 'multiplicador máximo da semana'}</small></div>
        <div class="bt-mult-track">
          <i class="fill" style={{ width: `${Math.min(1, b.weeklyDone / 16) * 100}%` }} />
          {MULT_STEPS.map(([at, m]) => <span key={at} class={b.weeklyDone >= at ? 'on' : ''} style={{ left: `${(at / 16) * 100}%` }}><b>x{m}</b><small>{at}</small></span>)}
        </div>
      </div>

      <div class="bt-sec">Abates <small>· {killsDone}/{b.weekly.length} concluídos</small></div>
      <div class="bt-week">
        {b.weekly.map((t) => {
          const done = t.kills >= t.need;
          return (
            <div key={t.monster} class={'bt-wrow' + (done ? ' done' : '')}>
              <span class="bt-face sm">{props.outfitOf(t.monster) ? <Portrait outfit={props.outfitOf(t.monster)!} size={32} /> : <i>?</i>}</span>
              <div class="bt-wtxt">
                <b>{t.monster}</b>
                <Bar v={t.kills} max={t.need} label={done ? '✔ Concluída' : `${n(t.kills)} / ${n(t.need)}`} done={done} />
              </div>
              {props.onHunt && !done && <button class="tb-button bt-go" onClick={() => props.onHunt!(t.monster)} title="Abre o Mapa de Caças na hunt com mais dessa criatura">🗺️ Hunt</button>}
            </div>
          );
        })}
        {!b.weekly.length && <p class="bt-muted">Nenhuma tarefa de abate nesta semana.</p>}
      </div>

      <div class="bt-sec">Entregas <small>· {delsDone}/{dels.length} · {WEEKLY_DELIVERY_HTP} pontos × multiplicador cada; os itens precisam estar na mochila</small></div>
      <div class="bt-week">
        {dels.map((dv, i) => {
          const have = props.have(dv.itemId);
          const ok = have >= dv.need;
          return (
            <div key={dv.itemId} class={'bt-wrow' + (dv.done ? ' done' : ok ? ' ready' : '')}>
              <span class="item-slot"><ItemImg typeId={dv.itemId} count={dv.need} /></span>
              <div class="bt-wtxt">
                <b>{dv.name}</b>
                <Bar v={dv.done ? dv.need : Math.min(have, dv.need)} max={dv.need} label={dv.done ? '✔ Entregue' : `${n(Math.min(have, dv.need))} / ${n(dv.need)} na mochila`} done={dv.done} />
              </div>
              {!dv.done && <button class="tb-button green" disabled={!ok} title={ok ? 'Entregar os itens' : `Faltam ${n(dv.need - have)} na mochila`} onClick={() => props.send('deliver', String(i))}>Entregar</button>}
            </div>
          );
        })}
        {!dels.length && <p class="bt-muted">Nenhuma entrega nesta semana.</p>}
      </div>

      <div class="bt-sec">Loja semanal <small>· você tem {n(b.htp)} pontos semanais</small></div>
      <ShopGrid items={HTP_SHOP} have={b.htp} onBuy={(id) => props.send('htpbuy', id)} />
    </div>
  );
}
